import {
    defaultIConfig,
    IChat,
    IChatRequest,
    IConfig,
    IMessage,
    IProject,
    IPrompt,
    IRelease,
    IShare,
    ISubagentBlock,
    ISummary,
} from "@/lib/types";
import { generateMockPrompts } from "@/lib/mocks";
import { CHAT_API_URL, CHAT_STOP_API_URL, GRAPHQL_API_URL } from "@/lib/utils";
import { DEFAULT_MODEL_ID, getModelById } from "@/lib/models";

export const CHATS_PER_PAGE = 20;

export const getChats = async (jwt: string, offset = 0, limit = CHATS_PER_PAGE): Promise<IChat[]> => {
    const query = `
        query GetChats($limit: Int, $offset: Int) {
            chats(limit: $limit, offset: $offset) {
                chatid
                email
                title
                favorite
                shareid
                project {
                    projectid
                }
                created
                lastmessage
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { limit, offset },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.chats.map(
        (chat: {
            chatid: string;
            email: string;
            title?: string;
            favorite: boolean;
            shareid?: string;
            project?: { projectid: string };
            created: string;
            lastmessage: string;
        }): IChat => ({
            chatid: chat.chatid,
            projectid: chat.project?.projectid,
            user: chat.email,
            title: chat.title,
            favorite: chat.favorite,
            shareid: chat.shareid,
            messages: [],
            created: new Date(chat.created),
            lastmessage: new Date(chat.lastmessage),
        })
    );
};

export const getChat = async (jwt: string, chatid: string): Promise<IChat | null> => {
    const query = `
        query GetChat($chatid: String!) {
            chat(chatid: $chatid) {
                chatid
                email
                title
                favorite
                shareid
                project {
                    projectid
                }
                created
                lastmessage
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { chatid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    const chat = result.data.chat;

    if (!chat) {
        return null;
    }

    return {
        chatid: chat.chatid,
        projectid: chat.project?.projectid,
        user: chat.email,
        title: chat.title,
        favorite: chat.favorite,
        shareid: chat.shareid,
        messages: [],
        created: new Date(chat.created),
        lastmessage: new Date(chat.lastmessage),
    };
};

export const getChatsByProjectId = async (jwt: string, projectid: string): Promise<IChat[]> => {
    const query = `
        query GetChatsByProjectId($projectid: String!) {
            project(projectid: $projectid) {
                chats {
                    chatid
                    email
                    title
                    favorite
                    shareid
                    created
                    lastmessage
                }
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { projectid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    // Return empty array if project not found
    if (!result.data.project) {
        return [];
    }

    // Map GraphQL response to IChat interface
    return result.data.project.chats.map(
        (chat: {
            chatid: string;
            email: string;
            title?: string;
            favorite: boolean;
            shareid?: string;
            created: string;
            lastmessage: string;
        }): IChat => ({
            chatid: chat.chatid,
            projectid: projectid,
            user: chat.email,
            title: chat.title,
            favorite: chat.favorite,
            shareid: chat.shareid,
            created: new Date(chat.created),
            lastmessage: new Date(chat.lastmessage),
        })
    );
};

export const saveConfig = (config: IConfig) => {
    if (window && window.localStorage) {
        const { canvasOpen: _, canvas: __, ...persistedConfig } = config;
        window.localStorage.setItem("FAIconfig", JSON.stringify(persistedConfig));
    }
};

export const loadConfig = (): IConfig => {
    if (typeof window !== "undefined") {
        const stored = JSON.parse(window.localStorage.getItem("FAIconfig") || "{}") as Partial<IConfig>;
        // Merge with defaults to ensure new fields have default values
        const config = { ...defaultIConfig, ...stored };
        // Validate selectedModel exists in available models, fall back to default if not
        if (!getModelById(config.selectedModel)) {
            config.selectedModel = DEFAULT_MODEL_ID;
        }
        return config;
    } else {
        return defaultIConfig;
    }
};

export const updateChat = async (jwt: string, chat: Partial<IChat> & Pick<IChat, "chatid">): Promise<IChat> => {
    const mutation = `
        mutation UpdateChat($chat: UpdateChatInput!) {
            updateChat(chat: $chat) {
                chatid
                email
                title
                favorite
                shareid
                created
                lastmessage
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: {
                chat: {
                    chatid: chat.chatid,
                    title: chat.title,
                    favorite: chat.favorite,
                },
            },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    const updated = result.data.updateChat;
    return {
        chatid: updated.chatid,
        user: updated.email,
        title: updated.title,
        favorite: updated.favorite,
        shareid: updated.shareid,
        messages: [],
        created: new Date(updated.created),
        lastmessage: new Date(updated.lastmessage),
    };
};

export const deleteChat = async (jwt: string, chatid: string): Promise<boolean> => {
    const mutation = `
        mutation DeleteChat($chatid: String!) {
            deleteChat(chatid: $chatid)
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: {
                chatid,
            },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.deleteChat;
};

export const getProjects = async (jwt: string): Promise<IProject[]> => {
    const query = `
        query GetProjects {
            projects {
                projectid
                email
                title
                favorite
                description
                instructions
                files {
                    uri
                }
                created
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ query }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    // Map GraphQL response to IProject interface
    return result.data.projects.map(
        (project: {
            projectid: string;
            email: string;
            title: string;
            favorite: boolean;
            description?: string;
            instructions?: string;
            files: { uri: string }[];
            created: string;
        }): IProject => ({
            projectid: project.projectid,
            user: project.email,
            title: project.title,
            favorite: project.favorite,
            description: project.description,
            instructions: project.instructions,
            files: project.files?.map((f) => ({ uri: f.uri })) || [],
            chats: [],
            created: new Date(project.created),
        })
    );
};

export const createProject = async (
    jwt: string,
    project: Pick<IProject, "title" | "description">
): Promise<IProject> => {
    const mutation = `
        mutation CreateProject($project: CreateProjectInput!) {
            createProject(project: $project) {
                projectid
                email
                title
                favorite
                description
                instructions
                created
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: {
                project: {
                    title: project.title,
                    description: project.description,
                },
            },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    // Map GraphQL response to IProject interface
    const created = result.data.createProject;
    return {
        projectid: created.projectid,
        user: created.email,
        title: created.title,
        favorite: created.favorite,
        description: created.description,
        instructions: created.instructions,
        chats: [],
        created: new Date(created.created),
    };
};

export const updateProject = async (
    jwt: string,
    project: Partial<IProject> & Pick<IProject, "projectid">
): Promise<IProject> => {
    const mutation = `
        mutation UpdateProject($project: UpdateProjectInput!) {
            updateProject(project: $project) {
                projectid
                email
                title
                favorite
                description
                instructions
                files {
                    uri
                }
                created
            }
        }
    `;

    // Build the variables, only including files if provided
    const variables: {
        project: {
            projectid: string;
            title?: string;
            favorite?: boolean;
            description?: string;
            instructions?: string;
            files?: { uri: string }[];
        };
    } = {
        project: {
            projectid: project.projectid,
            title: project.title,
            favorite: project.favorite,
            description: project.description,
            instructions: project.instructions,
        },
    };

    // Only include files if explicitly provided (to support file updates)
    if (project.files !== undefined) {
        variables.project.files = project.files.map((f) => ({ uri: f.uri }));
    }

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables,
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    const updated = result.data.updateProject;
    return {
        projectid: updated.projectid,
        user: updated.email,
        title: updated.title,
        favorite: updated.favorite,
        description: updated.description,
        instructions: updated.instructions,
        files: updated.files?.map((f: { uri: string }) => ({ uri: f.uri })) || [],
        chats: [],
        created: new Date(updated.created),
    };
};

export const deleteProject = async (jwt: string, projectid: string): Promise<boolean> => {
    const mutation = `
        mutation DeleteProject($projectid: String!) {
            deleteProject(projectid: $projectid)
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: {
                projectid,
            },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.deleteProject;
};

export const createShare = async (jwt: string, chatid: string): Promise<string> => {
    const mutation = `
        mutation ShareChat($chatid: String!) {
            shareChat(chatid: $chatid) {
                shareid
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: { chatid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.shareChat.shareid;
};

export const deleteShare = async (jwt: string, chatid: string): Promise<boolean> => {
    const mutation = `
        mutation DeleteShare($chatid: String!) {
            deleteShare(chatid: $chatid)
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: { chatid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.deleteShare;
};

function reconstructSubagentBlocks(messages: IMessage[]): IMessage[] {
    for (let i = 0; i < messages.length - 1; i++) {
        const assistantMsg = messages[i];
        const userMsg = messages[i + 1];

        if (assistantMsg.role !== "assistant" || userMsg.role !== "user") continue;

        // Collect all agent names referenced in the user message's subagent blocks
        const agentNames = new Set<string>();
        for (const b of userMsg.content) {
            if (b.subagentReasoning) agentNames.add(b.subagentReasoning.agentName);
            if (b.subagentToolUse) agentNames.add(b.subagentToolUse.agentName);
            if (b.subagentToolResult) agentNames.add(b.subagentToolResult.agentName);
            if (b.subagentText) agentNames.add(b.subagentText.agentName);
            if (b.subagentAuthRequired) agentNames.add(b.subagentAuthRequired.agentName);
        }

        if (agentNames.size === 0) continue;

        for (const block of assistantMsg.content) {
            if (!block.toolUse || !agentNames.has(block.toolUse.name)) continue;

            const subagentBlock: ISubagentBlock = {
                agentName: block.toolUse.name,
                reasoning: "",
                tools: [],
                text: "",
                isComplete: true,
            };

            for (const uBlock of userMsg.content) {
                if (uBlock.subagentReasoning?.agentName === block.toolUse.name) {
                    subagentBlock.reasoning += uBlock.subagentReasoning.reasoningText;
                } else if (uBlock.subagentToolUse?.agentName === block.toolUse.name) {
                    subagentBlock.tools.push({
                        toolName: uBlock.subagentToolUse.toolName,
                        toolInput: uBlock.subagentToolUse.toolInput,
                    });
                } else if (uBlock.subagentToolResult?.agentName === block.toolUse.name) {
                    const pending = subagentBlock.tools.find((t) => !t.status);
                    if (pending) {
                        pending.status = uBlock.subagentToolResult.status;
                        pending.resultPreview = uBlock.subagentToolResult.contentPreview;
                    }
                } else if (uBlock.subagentText?.agentName === block.toolUse.name) {
                    subagentBlock.text += uBlock.subagentText.text;
                }
            }

            block.subagentBlock = subagentBlock;
        }

        userMsg.content = userMsg.content.filter(
            (b) => !b.type?.startsWith("subagent_") || b.toolResult || b.text || b.subagentAuthRequired
        );
    }

    for (const msg of messages) {
        for (const block of msg.content) {
            delete block.subagentReasoning;
            delete block.subagentToolUse;
            delete block.subagentToolResult;
            delete block.subagentText;
            // Keep block.subagentAuthRequired — ContentBlocks renders it as AssistantOAuthConnect
        }
    }

    return messages;
}

export const getMessages = async (jwt: string, chatid: string): Promise<IMessage[]> => {
    const query = `
        query GetMessages($chatid: String!) {
            messages(chatid: $chatid) {
                messageid
                role
                stopped
                content {
                    contentid
                    type
                    text
                    document {
                        name
                        format
                        source {
                            s3Location {
                                uri
                            }
                        }
                    }
                    image {
                        format
                        source {
                            s3Location {
                                uri
                            }
                            thumbnail
                        }
                    }
                    reasoningContent {
                        reasoningText {
                            text
                            signature
                        }
                    }
                    toolUse {
                        toolUseId
                        name
                        input
                    }
                    toolResult {
                        toolUseId
                        status
                        content {
                            text
                            json
                            document {
                                name
                                format
                                source {
                                    s3Location {
                                        uri
                                    }
                                }
                            }
                            image {
                                format
                                source {
                                    s3Location {
                                        uri
                                    }
                                }
                            }
                        }
                    }
                    subagentReasoning {
                        agentName
                        reasoningText
                    }
                    subagentToolUse {
                        agentName
                        toolName
                        toolInput
                    }
                    subagentToolResult {
                        agentName
                        status
                        contentPreview
                    }
                    subagentText {
                        agentName
                        text
                    }
                    subagentAuthRequired {
                        agentName
                        provider
                        authorizationUrl
                    }
                }
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { chatid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    const messages: IMessage[] = result.data.messages.map(
        (message: {
            messageid: string;
            role: "user" | "assistant";
            stopped?: boolean;
            content: {
                contentid?: string;
                type?: string;
                text?: string;
                document?: {
                    name: string;
                    format: string;
                    source: { s3Location: { uri: string } };
                };
                image?: {
                    format: string;
                    source: { s3Location: { uri: string }; thumbnail?: string };
                };
                reasoningContent?: {
                    reasoningText?: { text: string; signature?: string };
                };
                toolUse?: {
                    toolUseId: string;
                    name: string;
                    input: unknown;
                };
                toolResult?: {
                    toolUseId: string;
                    status?: string;
                    content: {
                        text?: string;
                        json?: unknown;
                        document?: {
                            name: string;
                            format: string;
                            source: { s3Location: { uri: string } };
                        };
                        image?: {
                            format: string;
                            source: { s3Location: { uri: string } };
                        };
                    }[];
                };
                subagentReasoning?: { agentName: string; reasoningText: string };
                subagentToolUse?: { agentName: string; toolName: string; toolInput?: any };
                subagentToolResult?: { agentName: string; status: string; contentPreview: string };
                subagentText?: { agentName: string; text: string };
                subagentAuthRequired?: { agentName: string; provider: string; authorizationUrl: string };
            }[];
        }): IMessage => ({
            messageid: message.messageid,
            role: message.role,
            stopped: message.stopped ?? undefined,
            content: message.content.map((block) => ({
                contentid: block.contentid ?? undefined,
                type: (block.type as IMessage["content"][0]["type"]) ?? undefined,
                text: block.text,
                document: block.document
                    ? {
                          name: block.document.name,
                          format: block.document.format as IMessage["content"][0]["document"] extends {
                              format: infer F;
                          }
                              ? F
                              : never,
                          source: { s3Location: block.document.source.s3Location },
                      }
                    : undefined,
                image: block.image
                    ? {
                          format: block.image.format as IMessage["content"][0]["image"] extends { format: infer F }
                              ? F
                              : never,
                          source: {
                              s3Location: block.image.source.s3Location,
                              thumbnail: block.image.source.thumbnail,
                          },
                      }
                    : undefined,
                reasoningContent: block.reasoningContent
                    ? {
                          reasoningText: block.reasoningContent.reasoningText
                              ? {
                                    text: block.reasoningContent.reasoningText.text,
                                    signature: block.reasoningContent.reasoningText.signature ?? undefined,
                                }
                              : undefined,
                      }
                    : undefined,
                toolUse: block.toolUse ?? undefined,
                toolResult: block.toolResult
                    ? {
                          toolUseId: block.toolResult.toolUseId,
                          status: block.toolResult.status as "success" | "error" | undefined,
                          content: block.toolResult.content.map((c) => ({
                              text: c.text,
                              json: c.json,
                              document: c.document
                                  ? {
                                        name: c.document.name,
                                        format: c.document.format as IMessage["content"][0]["document"] extends {
                                            format: infer F;
                                        }
                                            ? F
                                            : never,
                                        source: { s3Location: c.document.source.s3Location },
                                    }
                                  : undefined,
                              image: c.image
                                  ? {
                                        format: c.image.format as IMessage["content"][0]["image"] extends {
                                            format: infer F;
                                        }
                                            ? F
                                            : never,
                                        source: { s3Location: c.image.source.s3Location },
                                    }
                                  : undefined,
                          })),
                      }
                    : undefined,
                subagentReasoning: block.subagentReasoning ?? undefined,
                subagentToolUse: block.subagentToolUse ?? undefined,
                subagentToolResult: block.subagentToolResult ?? undefined,
                subagentText: block.subagentText ?? undefined,
                subagentAuthRequired: block.subagentAuthRequired ?? undefined,
            })),
        })
    );

    return reconstructSubagentBlocks(messages);
};

let cachedPrompts: IPrompt[] | null = null;

export const getPrompts = async (): Promise<IPrompt[]> => {
    if (cachedPrompts) {
        return cachedPrompts;
    }
    // TODO graphql query for this
    const mockPrompts = generateMockPrompts();
    cachedPrompts = mockPrompts;
    return mockPrompts;
};

export const getLatestRelease = async (jwt: string): Promise<string | undefined> => {
    const query = `
        query GetLatestRelease {
            releases(limit: 1) {
                title
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ query }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.releases[0]?.title;
};

export const getReleases = async (jwt: string): Promise<IRelease[]> => {
    const query = `
        query GetReleases {
            releases {
                id
                title
                created
                summary
                items
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ query }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.releases.map(
        (release: { id: string; title: string; created: string; summary: string; items: string[] }): IRelease => ({
            id: release.id,
            title: release.title,
            created: new Date(release.created),
            summary: release.summary,
            items: release.items,
        })
    );
};

export const fetchStreamResponse = async (
    jwt: string,
    request: IChatRequest,
    abortSignal: AbortSignal
): Promise<ReadableStream<Uint8Array>> => {
    const response = await fetch(CHAT_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(request),
        signal: abortSignal,
    });

    if (!response.ok) {
        throw new Error(`Chat request failed: ${response.status} ${response.statusText}`);
    }

    if (!response.body) {
        throw new Error("No response body");
    }

    return response.body;
};

/**
 * Signal an explicit user stop for an in-flight streaming chat.
 *
 * The backend treats a dropped streaming connection as a PASSIVE disconnect
 * (it keeps the agent running and persists the full result). The Stop button
 * needs the opposite — cancel the run and persist the partial — so it signals
 * intent out-of-band on this dedicated endpoint before the stream fetch aborts.
 *
 * Best-effort and fire-and-forget: a failed stop must never throw into the UI.
 * The local partial is already committed by the caller, and the backend's
 * cancel is itself best-effort, so swallowing errors here is intentional.
 */
export const stopChat = async (jwt: string, chatid: string): Promise<void> => {
    try {
        await fetch(CHAT_STOP_API_URL, {
            method: "POST",
            credentials: "omit",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({ chatid }),
        });
    } catch (error) {
        console.warn(`stopChat failed for chatid=${chatid}:`, error);
    }
};

export const getSummary = async (jwt: string, text: string, length: number): Promise<ISummary> => {
    console.log(`${Date.now()} getSummary: text length=${text.length}, summary length=${length}`);

    const query = `
        query Summarize($text: String!, $length: Int!) {
            summarize(text: $text, length: $length)
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { text, length },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return { summary: result.data.summarize || "" };
};

export const getShareById = async (jwt: string, shareid: string): Promise<IShare> => {
    const query = `
        query GetShareById($shareid: String!) {
            share(shareid: $shareid) {
                shareid
                chatid
                email
                messages {
                    messageid
                    role
                    stopped
                    content {
                        contentid
                        type
                        text
                        document {
                            name
                            format
                            source {
                                s3Location {
                                    uri
                                }
                            }
                        }
                        image {
                            format
                            source {
                                s3Location {
                                    uri
                                }
                                thumbnail
                            }
                        }
                        reasoningContent {
                            reasoningText {
                                text
                                signature
                            }
                        }
                        toolUse {
                            toolUseId
                            name
                            input
                        }
                        toolResult {
                            toolUseId
                            status
                            content {
                                text
                                json
                                document {
                                    name
                                    format
                                    source {
                                        s3Location {
                                            uri
                                        }
                                    }
                                }
                                image {
                                    format
                                    source {
                                        s3Location {
                                            uri
                                        }
                                    }
                                }
                            }
                        }
                        subagentReasoning {
                            agentName
                            reasoningText
                        }
                        subagentToolUse {
                            agentName
                            toolName
                            toolInput
                        }
                        subagentToolResult {
                            agentName
                            status
                            contentPreview
                        }
                        subagentAuthRequired {
                            agentName
                            provider
                            authorizationUrl
                        }
                        subagentText {
                            agentName
                            text
                        }
                    }
                }
                created
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { shareid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    const share = result.data.share;

    if (!share) {
        throw new Error(`Share not found: ${shareid}`);
    }

    const mappedMessages: IMessage[] | undefined = share.messages?.map(
        (message: {
            messageid: string;
            role: "user" | "assistant";
            stopped?: boolean;
            content: {
                contentid?: string;
                type?: string;
                text?: string;
                document?: {
                    name: string;
                    format: string;
                    source: { s3Location: { uri: string } };
                };
                image?: {
                    format: string;
                    source: { s3Location: { uri: string }; thumbnail?: string };
                };
                reasoningContent?: {
                    reasoningText?: { text: string; signature?: string };
                };
                toolUse?: {
                    toolUseId: string;
                    name: string;
                    input: unknown;
                };
                toolResult?: {
                    toolUseId: string;
                    status?: string;
                    content: {
                        text?: string;
                        json?: unknown;
                        document?: {
                            name: string;
                            format: string;
                            source: { s3Location: { uri: string } };
                        };
                        image?: {
                            format: string;
                            source: { s3Location: { uri: string } };
                        };
                    }[];
                };
                subagentReasoning?: { agentName: string; reasoningText: string };
                subagentToolUse?: { agentName: string; toolName: string; toolInput?: any };
                subagentToolResult?: { agentName: string; status: string; contentPreview: string };
                subagentText?: { agentName: string; text: string };
                subagentAuthRequired?: { agentName: string; provider: string; authorizationUrl: string };
            }[];
        }): IMessage => ({
            messageid: message.messageid,
            role: message.role,
            stopped: message.stopped ?? undefined,
            content: message.content.map((block) => ({
                contentid: block.contentid ?? undefined,
                type: (block.type as IMessage["content"][0]["type"]) ?? undefined,
                text: block.text,
                document: block.document
                    ? {
                          name: block.document.name,
                          format: block.document.format as IMessage["content"][0]["document"] extends {
                              format: infer F;
                          }
                              ? F
                              : never,
                          source: { s3Location: block.document.source.s3Location },
                      }
                    : undefined,
                image: block.image
                    ? {
                          format: block.image.format as IMessage["content"][0]["image"] extends { format: infer F }
                              ? F
                              : never,
                          source: {
                              s3Location: block.image.source.s3Location,
                              thumbnail: block.image.source.thumbnail,
                          },
                      }
                    : undefined,
                reasoningContent: block.reasoningContent
                    ? {
                          reasoningText: block.reasoningContent.reasoningText
                              ? {
                                    text: block.reasoningContent.reasoningText.text,
                                    signature: block.reasoningContent.reasoningText.signature ?? undefined,
                                }
                              : undefined,
                      }
                    : undefined,
                toolUse: block.toolUse ?? undefined,
                toolResult: block.toolResult
                    ? {
                          toolUseId: block.toolResult.toolUseId,
                          status: block.toolResult.status as "success" | "error" | undefined,
                          content: block.toolResult.content.map((c) => ({
                              text: c.text,
                              json: c.json,
                              document: c.document
                                  ? {
                                        name: c.document.name,
                                        format: c.document.format as IMessage["content"][0]["document"] extends {
                                            format: infer F;
                                        }
                                            ? F
                                            : never,
                                        source: { s3Location: c.document.source.s3Location },
                                    }
                                  : undefined,
                              image: c.image
                                  ? {
                                        format: c.image.format as IMessage["content"][0]["image"] extends {
                                            format: infer F;
                                        }
                                            ? F
                                            : never,
                                        source: { s3Location: c.image.source.s3Location },
                                    }
                                  : undefined,
                          })),
                      }
                    : undefined,
                subagentReasoning: block.subagentReasoning ?? undefined,
                subagentToolUse: block.subagentToolUse ?? undefined,
                subagentToolResult: block.subagentToolResult ?? undefined,
                subagentText: block.subagentText ?? undefined,
                subagentAuthRequired: block.subagentAuthRequired ?? undefined,
            })),
        })
    );

    return {
        shareid: share.shareid,
        chatid: share.chatid,
        email: share.email,
        messages: mappedMessages ? reconstructSubagentBlocks(mappedMessages) : undefined,
        created: new Date(share.created),
    };
};

export interface IPresignResult {
    presignedUrl: string;
    s3Uri: string;
}

/**
 * Get a presigned URL and S3 URI for uploading a file to S3
 */
export const getPresignedUrl = async (jwt: string, filename: string): Promise<IPresignResult> => {
    const query = `
        query Presign($filename: String!) {
            presign(filename: $filename) {
                presignedUrl
                s3Uri
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { filename },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.presign;
};

export const S3_UPLOADS_BUCKET = process.env.NEXT_PUBLIC_S3_UPLOADS_BUCKET || "fender-ai-uploads-active";
export const S3_DOWNLOADS_BUCKET =
    process.env.NEXT_PUBLIC_S3_DOWNLOADS_BUCKET || "fai-use1-active-fai-intelligence-docs";

/**
 * Get a presigned URL for downloading a file from S3
 */
export const getPresignedDownloadUrl = async (
    jwt: string,
    key: string,
    bucket?: string,
    download?: boolean
): Promise<string> => {
    const query = `
        query PresignDownload($key: String!, $bucket: String, $download: Boolean) {
            presignDownload(key: $key, bucket: $bucket, download: $download)
        }
    `;

    const variables: { key: string; bucket?: string; download?: boolean } = { key };
    if (bucket !== undefined) {
        variables.bucket = bucket;
    }
    if (download !== undefined) {
        variables.download = download;
    }

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.presignDownload;
};

/**
 * Delete a file from S3
 */
export const deleteS3File = async (jwt: string, filename: string): Promise<boolean> => {
    const mutation = `
        mutation DeleteFile($filename: String!) {
            deleteFile(filename: $filename)
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: { filename },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.deleteFile;
};
